import { useLocation } from "react-router-dom";
import styles from "./index.module.css";
import DocLayout from "../../components/DocLayout";

const PrivacyPolicy = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const ref = searchParams.get("ref");
  return (
    <DocLayout callbackUrl={ref}>
      <div className={styles.container}>
        <h1>QR Studio Privacy Policy</h1>
        <p>
          <strong>Last Updated:</strong> December 1, 2025
        </p>

        <p>
          This Privacy Policy explains how QR Studio LLC ("QR Studio," "we,"
          "us," or "our") collects, uses, discloses, and protects information
          when you access or use our digital products and services including
          websites, virtual reality (VR) and augmented reality (AR)
          applications, mobile applications, software solutions, analytics
          platforms, and learning management systems (collectively, the
          "Services").
        </p>

        <p>By using our Services, you agree to this Privacy Policy.</p>

        <h2>1. Scope</h2>
        <p>
          This Privacy Policy applies to information we process in connection
          with the Services. It does not cover third-party websites, products,
          or services that may be linked from the Services.
        </p>

        <h2>2. Information We Collect</h2>
        <p>
          We collect information in three main ways: (a) information you
          provide, (b) information collected automatically when you use the
          Services, and (c) information provided by Institutions or integrated
          systems.
        </p>

        <h3>2.1 Information you provide</h3>
        <ul>
          <li>
            Account and profile information, such as name, email address,
            username, role (e.g., student, instructor, administrator), and
            password or authentication token.
          </li>
          <li>
            Support and communications, such as requests submitted to our
            support channels, emails, and feedback.
          </li>
          <li>
            Institutional or course information you submit, such as class
            identifiers or cohort labels, where enabled by the Institution.
          </li>
          <li>
            User-generated content you upload or submit to the Services, if
            applicable (e.g., documents, notes, or comments).
          </li>
        </ul>

        <h3>2.2 Information collected automatically</h3>
        <ul>
          <li>
            Device and technical data, such as device type/model (including
            compatible VR headsets), operating system, browser type, language
            settings, and approximate location derived from IP address.
          </li>
          <li>
            Usage and log data, such as access times, pages viewed, actions
            taken within the Services, error logs, and performance diagnostics.
          </li>
          <li>
            Session identifiers and similar technologies (e.g., cookies or local
            storage) used to maintain sessions, security, and preferences.
          </li>
        </ul>

        <h3>2.3 Learning and performance data</h3>
        <p>
          When the Services are used for training or education, we may process
          learning and performance data ("Training Data") generated by users
          while interacting with connected XR/VR/Mobile or Desktop applications
          and the platform.
        </p>
        <ul>
          <li>
            Progress and completion status (e.g., module completion, timestamps,
            attempt counts).
          </li>
          <li>
            Performance metrics (e.g., scores, rubrics, pass/fail indicators,
            and instructor-defined evaluation data).
          </li>
          <li>
            Interaction events (e.g., key steps completed, confirmations, and
            other in-simulation events recorded for assessment).
          </li>
        </ul>
        <p>
          Training Data is typically owned or controlled by the Institution, and
          QR Studio processes it to provide analytics, reporting,
          troubleshooting, and service improvements as permitted by contract and
          applicable law.
        </p>

        <h2>3. How We Use Information</h2>
        <ul>
          <li>
            Provide, operate, maintain, and secure the Services, including
            authentication and access control to connected applications.
          </li>
          <li>
            Generate analytics and reports for Institutions and authorized
            administrators.
          </li>
          <li>
            Provide customer support, respond to requests, and communicate about
            service issues or updates.
          </li>
          <li>
            Improve the Services, including diagnosing bugs, optimizing
            performance, and developing new features.
          </li>
          <li>Comply with legal obligations and enforce our agreements.</li>
        </ul>

        <h2>4. Legal Bases for Processing (where applicable)</h2>
        <p>
          If you are located in a jurisdiction that requires a legal basis
          (e.g., the EU/EEA), QR Studio processes personal data under one or
          more of the following bases:
        </p>
        <ul>
          <li>
            Performance of a contract (providing the Services under an agreement
            with an Institution or enterprise customer).
          </li>
          <li>
            Legitimate interests (operating and securing the Services,
            preventing fraud, and improving product reliability), balanced
            against your rights.
          </li>
          <li>Compliance with legal obligations.</li>
          <li>
            Consent, where required (for example, certain optional cookies or
            features).
          </li>
        </ul>

        <h2>5. How We Share Information</h2>

        <h3>5.1 With Institutions and authorized users</h3>
        <p>
          Institutions and their authorized instructors/administrators may
          access Training Data, user rosters, and reports for their authorized
          users, consistent with their internal policies and applicable law
          (including FERPA where applicable).
        </p>

        <h3>5.2 With service providers</h3>
        <p>
          We use vetted service providers to help operate the Services (for
          example, cloud hosting, backups, monitoring, analytics, and customer
          support tools). These providers are permitted to process information
          only to perform services for us and must protect it under contractual
          obligations.
        </p>
        <ul>
          <li>
            Cloud infrastructure and hosting providers (e.g., Microsoft Azure).
          </li>
          <li>Security monitoring, logging, and incident response tooling.</li>
          <li>Customer support and ticketing systems.</li>
        </ul>

        <h3>5.3 For legal, safety, and compliance</h3>
        <p>
          We may disclose information if we believe it is reasonably necessary
          to (a) comply with applicable law or legal process, (b) protect the
          security, rights, or property of QR Studio, our users, or others, or
          (c) investigate fraud or security issues.
        </p>

        <h3>5.4 Business transfers</h3>
        <p>
          If QR Studio is involved in a merger, acquisition, financing,
          reorganization, bankruptcy, or sale of assets, information may be
          transferred as part of that transaction, subject to appropriate
          confidentiality protections.
        </p>

        <h2>6. Data Retention</h2>
        <p>
          We retain personal data only for as long as necessary to provide the
          Services, meet contractual commitments to Institutions, comply with
          legal obligations, resolve disputes, and enforce our agreements.
        </p>
        <p>
          Institutions may request deletion or return of institutional data
          according to the applicable contract, Data Processing Agreement (if
          any), and our operational capabilities. Backups may persist for a
          limited period consistent with our backup and disaster recovery
          practices.
        </p>

        <h2>7. Security</h2>
        <p>
          We maintain administrative, technical, and physical safeguards
          designed to protect information against unauthorized access, loss,
          misuse, or alteration. Security measures may include access controls,
          encryption in transit, and monitoring. No method of transmission or
          storage is 100% secure; therefore, we cannot guarantee absolute
          security.
        </p>

        <h2>8. Education and Children’s Privacy</h2>

        <h3>8.1 FERPA (United States)</h3>
        <p>
          For Institutions subject to the Family Educational Rights and Privacy
          Act (FERPA), certain Training Data may constitute “education records.”
          In those cases, QR Studio acts as a “school official” with a
          “legitimate educational interest” and uses such data only as directed
          by the Institution and in accordance with applicable agreements and
          FERPA requirements.
        </p>

        <h3>8.2 COPPA (United States)</h3>
        <p>
          The Services are not intended for use by children under 13 outside of
          Institution-managed accounts. We do not knowingly collect personal
          information directly from children under 13. If access is provided to
          children under 13 through an Institution, the Institution is
          responsible for obtaining any required parental or guardian consent in
          accordance with the Children’s Online Privacy Protection Act (COPPA).
        </p>

        <h3>8.3 Minors under 18</h3>
        <p>
          Where minors under 18 are permitted to use the Services, access must
          be provisioned and managed by the Institution, which is responsible
          for obtaining any required consents under applicable law.
        </p>

        <h2>9. International Data Transfers</h2>
        <p>
          QR Studio is based in the United States, and information may be
          processed in the United States or other locations where our service
          providers operate. Where required by law, we use appropriate transfer
          safeguards (such as standard contractual clauses) for cross-border
          data transfers.
        </p>

        <h2>10. Your Rights and Choices</h2>
        <p>
          Depending on your location and relationship to an Institution, you may
          have rights to access, correct, delete, or restrict the processing of
          your personal data, and to object to certain processing. You may also
          have the right to data portability and to withdraw consent where
          processing is based on consent.
        </p>
        <p>
          If your account is managed by an Institution, please direct privacy
          requests to your Institution in the first instance. We will support
          the Institution in responding to requests as required by contract and
          applicable law.
        </p>

        <h3>10.1 Cookies and similar technologies</h3>
        <p>
          We use cookies and similar technologies to maintain sessions, support
          authentication, remember preferences, and help secure the Services.
          You may be able to control cookies through your browser settings;
          however, disabling cookies may affect functionality.
        </p>

        <h2>11. De-identified and Aggregated Data</h2>
        <p>
          We may create de-identified or aggregated data from information we
          process. We may use and disclose such data for product improvement,
          analytics, research, and benchmarking, provided it cannot reasonably
          be used to identify an individual.
        </p>

        <h2>12. Changes to This Privacy Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. If we make
          material changes, we will provide notice through the Services or other
          reasonable means. The “Last Updated” date at the top indicates when
          this Privacy Policy was most recently revised.
        </p>

        <h2>13. Contact Us</h2>
        <p>
          If you have questions about this Privacy Policy or our privacy
          practices, please contact:
        </p>

        <address>
          QR Studio LLC
          <br />
          30 North Gould Street, Ste N
          <br />
          Sheridan, WY 82801
          <br />
          United States
          <br />
          Email: <a href="mailto:br@qrstudioai.com">br@qrstudioai.com</a>
          <br />
          Website:{" "}
          <a
            href="https://qrstudioai.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            qrstudioai.com
          </a>
        </address>
      </div>
    </DocLayout>
  );
};

export default PrivacyPolicy;
